import React from 'react';
import { Link } from 'react-router-dom';
import { PageTransition } from '@/components/motion';

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-bold text-foreground mb-3">{title}</h2>
      <div className="text-muted-foreground text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Background decoration */}
      <div className="fixed inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-neon/5 pointer-events-none" />

      {/* Navbar */}
      <header className="relative z-10 flex items-center justify-between px-6 h-16 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-7 h-7 text-neon group-hover:text-green-400 transition-colors">
            <EyeIcon />
          </div>
          <span className="text-foreground font-bold text-lg">Proton</span>
          <span className="text-neon text-xs font-semibold uppercase tracking-widest ml-1 hidden sm:block">Trading</span>
        </Link>
        <Link to="/" className="text-neon hover:text-green-400 font-semibold text-sm transition-colors">
          Back to Home
        </Link>
      </header>

      {/* Content */}
      <main className="relative z-10 flex-1 flex justify-center px-4 py-12">
        <PageTransition className="w-full max-w-3xl">
          {/* Heading */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-extrabold text-foreground mb-2">Terms of Service</h1>
            <p className="text-muted-foreground text-sm">
              Please read these terms carefully before using the Proton Trading platform.
            </p>
          </div>

          {/* Terms Card */}
          <div className="rounded-2xl border border-white/8 bg-card/60 backdrop-blur-sm p-8 sm:p-10 space-y-10">

            {/* Last Updated */}
            <p className="text-xs text-muted-foreground border-b border-border pb-4">
              <span className="font-semibold text-foreground">Last Updated:</span> March 7, 2026
            </p>

            {/* 1. Acceptance of Terms */}
            <Section id="acceptance" title="1. Acceptance of Terms">
              <p>
                By accessing or using the Proton Trading platform ("Platform"), including any associated
                websites, applications, APIs, and services (collectively, the "Service"), you acknowledge
                that you have read, understood, and agree to be bound by these Terms of Service ("Terms").
                If you do not agree to these Terms, you must not access or use the Service.
              </p>
              <p>
                Your continued use of the Service following any modifications to these Terms constitutes
                your acceptance of the revised Terms. We encourage you to review this page periodically
                for updates.
              </p>
            </Section>

            {/* 2. Description of Service */}
            <Section id="description" title="2. Description of Service">
              <p>
                Proton is an automated trading bot platform that enables users to create, configure,
                backtest, and deploy algorithmic trading strategies. The Platform connects to
                third-party brokerage accounts to execute trades on behalf of users based on
                predefined strategies and parameters.
              </p>
              <p>
                The Service includes, but is not limited to: strategy building tools, backtesting
                engines, real-time market data visualization, portfolio management dashboards,
                trade history tracking, and broker account integration.
              </p>
              <p>
                Proton is a technology platform that provides tools for automated trading.
                Proton does not provide financial advice, investment recommendations, or
                brokerage services.
              </p>
            </Section>

            {/* 3. Account Registration and Eligibility */}
            <Section id="eligibility" title="3. Account Registration and Eligibility">
              <p>
                To use the Service, you must register for an account and meet the following
                eligibility requirements:
              </p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>You must be at least 18 years of age or the age of legal majority in your jurisdiction.</li>
                <li>You must provide accurate, complete, and current information during registration and keep your account information up to date.</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials, including your password.</li>
                <li>You are solely responsible for all activity that occurs under your account.</li>
                <li>You must have the legal capacity to enter into a binding agreement.</li>
              </ul>
              <p>
                Proton reserves the right to refuse service, suspend, or terminate accounts at its
                sole discretion if any information provided is found to be inaccurate, incomplete,
                or fraudulent.
              </p>
            </Section>

            {/* 4. Trading Risks and Disclaimers */}
            <Section id="risks" title="4. Trading Risks and Disclaimers">
              <div className="rounded-xl bg-red-500/5 border border-red-500/20 p-4 space-y-3">
                <p className="text-red-400 font-semibold text-sm uppercase tracking-wide">
                  Important Risk Disclosure
                </p>
                <p className="text-red-400/90">
                  Trading in financial markets involves substantial risk of loss and is not suitable
                  for every individual. You should carefully consider whether trading is appropriate
                  for you in light of your financial condition, experience, and risk tolerance. You
                  may sustain a loss of some or all of your invested capital. Do not trade with
                  money you cannot afford to lose.
                </p>
              </div>
              <p>
                <strong className="text-foreground">Past performance does not guarantee future results.</strong>{' '}
                Any backtesting results, historical performance data, strategy performance metrics,
                or simulated trading results displayed on the Platform are hypothetical in nature
                and are provided for informational purposes only. Actual trading results may differ
                materially from backtested or simulated results.
              </p>
              <p>
                <strong className="text-foreground">The Platform does not provide financial advice.</strong>{' '}
                Nothing on the Platform constitutes investment advice, financial advice, trading
                advice, or any other form of professional advice. You should consult with a
                qualified financial advisor before making any investment decisions.
              </p>
              <p>
                <strong className="text-foreground">Paper trading vs. live trading.</strong>{' '}
                The Platform may offer paper trading (simulated trading) features. Paper trading
                results do not reflect actual market conditions such as slippage, partial fills,
                latency, or liquidity constraints. Performance achieved through paper trading
                should not be considered indicative of results that would be achieved in live
                trading with real capital.
              </p>
              <p>
                Automated trading strategies may malfunction, produce unexpected results, or
                fail to execute trades as intended due to software errors, connectivity issues,
                market conditions, or other factors. You assume all risk associated with the
                use of automated trading strategies through the Platform.
              </p>
            </Section>

            {/* 5. Broker Connections and Third-Party Services */}
            <Section id="broker" title="5. Broker Connections and Third-Party Services">
              <p>
                The Platform allows you to connect your own brokerage accounts through
                third-party APIs and integrations. You acknowledge and agree to the following:
              </p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>Proton is not a broker-dealer, investment advisor, or financial institution. Proton does not hold, custody, or manage your funds.</li>
                <li>You are solely responsible for your relationship with your chosen brokerage provider, including compliance with their terms of service and applicable regulations.</li>
                <li>Proton does not guarantee the availability, reliability, or accuracy of any third-party brokerage APIs or data feeds.</li>
                <li>Any credentials or API keys you provide to connect your brokerage account are your responsibility. You should only connect accounts through the official integration methods provided by the Platform.</li>
                <li>Proton is not responsible for any actions taken by third-party brokers, including but not limited to order rejections, delays, or account restrictions.</li>
              </ul>
            </Section>

            {/* 6. User Conduct */}
            <Section id="conduct" title="6. User Conduct">
              <p>
                By using the Service, you agree that you will not:
              </p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>Engage in any form of market manipulation, including but not limited to spoofing, layering, wash trading, or pump-and-dump schemes.</li>
                <li>Use the Platform to violate any applicable local, state, national, or international laws or regulations, including securities laws and financial regulations.</li>
                <li>Attempt to gain unauthorized access to the Platform, other user accounts, or any systems or networks connected to the Service.</li>
                <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Platform or any proprietary algorithms.</li>
                <li>Use the Platform to transmit malicious code, viruses, or any other harmful technology.</li>
                <li>Resell, redistribute, or sublicense access to the Platform without express written authorization from Proton.</li>
              </ul>
              <p>
                You are solely responsible for ensuring that your use of the Platform and any
                trading activity conducted through it complies with all applicable laws and
                regulations in your jurisdiction.
              </p>
            </Section>

            {/* 7. Intellectual Property */}
            <Section id="ip" title="7. Intellectual Property">
              <p>
                All content, features, and functionality of the Platform -- including but not
                limited to the software, source code, algorithms, trading strategies, user
                interface designs, graphics, logos, trademarks, and documentation -- are the
                exclusive property of Proton and its licensors and are protected by copyright,
                trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p>
                You are granted a limited, non-exclusive, non-transferable, revocable license
                to access and use the Service for your personal, non-commercial use in
                accordance with these Terms. This license does not include the right to
                modify, copy, distribute, sell, or create derivative works based on the
                Platform or its content.
              </p>
            </Section>

            {/* 8. Limitation of Liability */}
            <Section id="liability" title="8. Limitation of Liability">
              <p>
                To the maximum extent permitted by applicable law, Proton and its officers,
                directors, employees, agents, affiliates, and licensors shall not be liable
                for any indirect, incidental, special, consequential, or punitive damages,
                including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>Financial losses resulting from trades executed through the Platform, whether automated or manual.</li>
                <li>Losses arising from system downtime, outages, maintenance windows, or service interruptions.</li>
                <li>Losses resulting from broker API failures, connectivity issues, or third-party service disruptions.</li>
                <li>Losses due to inaccurate or delayed market data.</li>
                <li>Losses arising from unauthorized access to your account due to your failure to safeguard your credentials.</li>
              </ul>
              <p>
                The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties
                of any kind, whether express or implied, including but not limited to implied
                warranties of merchantability, fitness for a particular purpose, and
                non-infringement.
              </p>
              <p>
                In no event shall Proton's total aggregate liability exceed the amount you
                have paid to Proton for the Service in the twelve (12) months preceding the
                event giving rise to the claim.
              </p>
            </Section>

            {/* 9. Indemnification */}
            <Section id="indemnification" title="9. Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless Proton and its officers,
                directors, employees, agents, affiliates, and licensors from and against any
                and all claims, damages, losses, liabilities, costs, and expenses (including
                reasonable attorneys' fees) arising out of or relating to:
              </p>
              <ul className="list-disc list-inside space-y-1.5 pl-2">
                <li>Your use of the Service or any activity conducted through your account.</li>
                <li>Your violation of these Terms or any applicable law or regulation.</li>
                <li>Your trading activities and any resulting financial losses or gains.</li>
                <li>Any dispute between you and a third-party broker or service provider.</li>
                <li>Your negligence or willful misconduct.</li>
              </ul>
            </Section>

            {/* 10. Data Privacy */}
            <Section id="privacy" title="10. Data Privacy">
              <p>
                Your privacy is important to us. Our collection, use, and disclosure of personal
                information is governed by our{' '}
                <Link to="/privacy" className="text-neon hover:text-green-400 underline underline-offset-2 transition-colors">
                  Privacy Policy
                </Link>
                , which is incorporated into these Terms by reference.
              </p>
              <p>
                By using the Service, you consent to the collection and use of your information
                as described in the Privacy Policy. You acknowledge that you are responsible for
                reviewing the Privacy Policy and understanding how your data is handled.
              </p>
            </Section>

            {/* 11. Termination */}
            <Section id="termination" title="11. Termination">
              <p>
                Either party may terminate this agreement at any time. You may terminate your
                account by contacting support or using the account deletion feature within the
                Platform settings.
              </p>
              <p>
                Proton reserves the right to suspend or terminate your access to the Service
                at any time, with or without cause, and with or without notice, including but
                not limited to cases where you violate these Terms or engage in activity that
                Proton deems harmful to the Platform, other users, or third parties.
              </p>
              <p>
                Upon termination, your right to use the Service will immediately cease. Any
                provisions of these Terms that by their nature should survive termination
                shall remain in effect, including but not limited to Sections 4, 7, 8, 9,
                and 14.
              </p>
              <p>
                You are responsible for closing any open positions and disconnecting any
                linked brokerage accounts prior to account termination. Proton shall not be
                liable for any trading activity that occurs before your account is fully
                terminated.
              </p>
            </Section>

            {/* 12. Modifications to Terms */}
            <Section id="modifications" title="12. Modifications to Terms">
              <p>
                Proton reserves the right to modify these Terms at any time. When we make
                material changes, we will update the "Last Updated" date at the top of this
                page and, where appropriate, provide additional notice such as an in-app
                notification or email.
              </p>
              <p>
                Your continued use of the Service after any modifications to these Terms
                constitutes your acceptance of the revised Terms. If you do not agree with
                the updated Terms, you must discontinue your use of the Service.
              </p>
            </Section>

            {/* 13. Governing Law */}
            <Section id="governing-law" title="13. Governing Law">
              <p>
                These Terms shall be governed by and construed in accordance with the laws
                of the United States, without regard to its conflict of law provisions.
                Any disputes arising out of or relating to these Terms or the Service shall
                be resolved in the federal or state courts located within the United States,
                and you consent to the personal jurisdiction of such courts.
              </p>
              <p>
                If any provision of these Terms is found to be unenforceable or invalid by
                a court of competent jurisdiction, that provision shall be limited or
                eliminated to the minimum extent necessary, and the remaining provisions
                shall remain in full force and effect.
              </p>
            </Section>

            {/* 14. Contact Information */}
            <Section id="contact" title="14. Contact Information">
              <p>
                If you have any questions, concerns, or requests regarding these Terms of
                Service, please contact us at:
              </p>
              <div className="rounded-xl bg-card border border-border p-4">
                <p className="text-foreground font-semibold">Proton Trading</p>
                <p>
                  Email:{' '}
                  <a href="mailto:support@tradeproton.com" className="text-neon hover:text-green-400 underline underline-offset-2 transition-colors">
                    support@tradeproton.com
                  </a>
                </p>
              </div>
            </Section>
          </div>

          {/* Footer disclaimer */}
          <p className="mt-8 text-center text-xs text-slate-600">
            Trading involves substantial risk of loss. Past performance does not guarantee future results.
          </p>
        </PageTransition>
      </main>
    </div>
  );
}
